import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import App from '../App'
import WarmingUpScreen, { type BootStatus } from './WarmingUpScreen'
import ErrorFallback from './ErrorFallback'
import { checkDatabaseReady, waitForBackend } from '../lib/waitForBackend'
import { initCsrf } from '../lib/api'

type AppStatus = BootStatus | 'ready'

// A paused database comes back only when it is restored by hand, so poll gently
// and carry on by itself once it answers — the user never has to refresh.
const UNAVAILABLE_RECHECK_MS = 30_000

/**
 * Newsreader (font-headline) and Material Symbols are app chrome only — the
 * pre-gate pages (landing, legal) never render them. They used to sit in
 * index.html as render-blocking stylesheets on every page's critical path;
 * loading them here keeps them off it. Inter and IBM Plex Serif are self-hosted
 * in src/fonts.css and are not touched by this.
 */
const APP_FONT_HREFS = [
  // Newsreader is body/heading text: `swap` is right — a fallback face is better
  // than invisible text during load.
  'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;0,6..72,700;1,6..72,400&display=swap',
  // Material Symbols is a LIGATURE icon font — its elements contain the icon
  // name as text ("settings", "logout", "arrow_back"). Under `swap` those words
  // render literally until the font arrives. While these stylesheets sat in
  // index.html they were render-blocking, so that window could never be seen;
  // loading them here makes it reachable, so use `block` (brief invisible period,
  // then swap) to keep raw ligature names off screen.
  'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=block',
]

function loadAppFonts() {
  // Preconnect belongs here rather than in index.html: these hosts are only ever
  // contacted on the app path, so warming the connection from the pre-gate pages
  // would open a socket the landing page never uses.
  for (const [href, crossOrigin] of [
    ['https://fonts.googleapis.com', false],
    ['https://fonts.gstatic.com', true],
  ] as const) {
    if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) continue
    const hint = document.createElement('link')
    hint.rel = 'preconnect'
    hint.href = href
    if (crossOrigin) hint.crossOrigin = 'anonymous'
    document.head.appendChild(hint)
  }

  for (const href of APP_FONT_HREFS) {
    if (document.head.querySelector(`link[href="${href}"]`)) continue
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    document.head.appendChild(link)
  }
}

export default function AppBootstrap() {
  const [status, setStatus] = useState<AppStatus>('booting')

  useEffect(() => {
    let mounted = true

    loadAppFonts()

    const warmingTimer = setTimeout(() => {
      if (mounted) setStatus('warming')
    }, 8_000)

    ;(async () => {
      const ok = await waitForBackend()
      if (!mounted) return

      if (!ok) {
        setStatus('error')
        return
      }

      let db = await checkDatabaseReady()
      while (db === 'unavailable') {
        if (!mounted) return
        clearTimeout(warmingTimer)
        setStatus('unavailable')
        await new Promise((r) => setTimeout(r, UNAVAILABLE_RECHECK_MS))
        if (!mounted) return
        db = await checkDatabaseReady(1)
      }
      if (!mounted) return

      await initCsrf()
      if (!mounted) return

      clearTimeout(warmingTimer)
      setStatus('ready')
    })()

    return () => {
      mounted = false
      clearTimeout(warmingTimer)
    }
  }, [])

  if (status === 'ready') {
    return (
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <App />
      </Sentry.ErrorBoundary>
    )
  }

  return <WarmingUpScreen status={status} />
}
